import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  InputAdornment,
  Alert,
} from '@mui/material';
import { Phone, CheckCircle, X } from 'lucide-react';
import type { RegionConfig } from '../../config/regions';

interface TestCallDialogProps {
  open: boolean;
  onClose: () => void;
  onCall: (phoneNumber: string) => Promise<void>;
  isLoading: boolean;
  remainingCalls: number;
  maxCalls: number;
  regionConfig: RegionConfig;
}

const TestCallDialog: React.FC<TestCallDialogProps> = ({
  open,
  onClose,
  onCall,
  isLoading,
  remainingCalls,
  maxCalls,
  regionConfig,
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [calledNumber, setCalledNumber] = useState('');

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
    setError(null);
  };

  const handleCall = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      return;
    }

    const cleaned = phoneNumber.replace(/[\s\-\(\)\.]/g, '');
    if (!regionConfig.phoneValidation.regex.test(cleaned)) {
      setError(regionConfig.phoneValidation.errorMessage);
      return;
    }

    try {
      const normalized = regionConfig.normalizePhone(cleaned);
      await onCall(normalized);
      setCalledNumber(normalized);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initiate call');
    }
  };

  const handleClose = () => {
    setPhoneNumber('');
    setError(null);
    setSuccess(false);
    setCalledNumber('');
    onClose();
  };

  const formatPhoneDisplay = (phone: string): string => {
    // Format based on region
    if (regionConfig.code === 'AU' && phone.startsWith('+61') && phone.length === 12) {
      return `${phone.slice(0, 3)} ${phone.slice(3, 6)} ${phone.slice(6, 9)} ${phone.slice(9)}`;
    }
    if (regionConfig.code === 'US' && phone.startsWith('+1') && phone.length === 12) {
      return `${phone.slice(0, 2)} (${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;
    }
    return phone;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Phone size={20} />
            <Typography variant="h6" component="span">
              {success ? 'Call Initiated!' : 'Test Your Call'}
            </Typography>
          </Box>
          <Button onClick={handleClose} sx={{ minWidth: 'auto', p: 0.5 }} color="inherit">
            <X size={20} />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <CheckCircle size={32} color="#4caf50" />
            </Box>
            <Typography variant="body1" gutterBottom>
              You will receive a call from our AI agent shortly at:
            </Typography>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
              {formatPhoneDisplay(calledNumber)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Please keep your phone nearby and answer when it rings.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your phone number to receive a test call from our AI voice agent.
            </Typography>

            <TextField
              fullWidth
              label="Phone Number"
              placeholder={regionConfig.phoneValidation.placeholder}
              value={phoneNumber}
              onChange={handlePhoneChange}
              error={!!error}
              helperText={
                error ||
                `Supported formats: ${regionConfig.phoneValidation.exampleFormats.join(', ')}`
              }
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography color="text.secondary">{regionConfig.countryCode}</Typography>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {remainingCalls < maxCalls && (
              <Alert severity={remainingCalls === 0 ? 'warning' : 'info'} sx={{ mb: 2 }}>
                {remainingCalls === 0
                  ? 'You have used all your test calls.'
                  : `${remainingCalls} of ${maxCalls} test calls remaining`}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {success ? (
          <Button onClick={handleClose} variant="contained" disableElevation fullWidth>
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleCall}
              variant="contained"
              disableElevation
              disabled={isLoading || remainingCalls === 0 || !phoneNumber.trim()}
              startIcon={isLoading ? <CircularProgress size={16} /> : <Phone size={16} />}
            >
              {isLoading ? 'Calling...' : 'Call Me Now'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TestCallDialog;
